// AddEventForm — slim container/orchestrator for the "Add {event}" modal.
//
// Behavior-preserving decomposition of the original 741-LOC component
// (issue #240, spec task 13.7). This container keeps the SAME module path,
// the SAME default export `AddEventForm`, the SAME props
// ({ eventType, trip, onAddEvent, onCancel } — `trip` accepted but unused,
// aliased `_trip` exactly as the source did), and the SAME rendered DOM.
//
// It composes the extracted, independently-tested units:
//   - useEventDraft  : owns formData/errors + the change handler + the label
//   - useSaveEvent   : owns isSaving + the validate → build payload → await
//                      onAddEvent submit flow
//   - AddEventModalShell : the overlay/backdrop, modal container, and header
//   - EventFields    : the per-type field group dispatcher (Form block)
//   - AddEventFooter : the Cancel + primary submit (Action Buttons block)
//
// The render order (Header → Form → Action Buttons) is preserved exactly: the
// shell renders the header, then its children render EventFields followed by
// AddEventFooter, matching the source so the 13.1 characterization snapshots
// and contract test continue to match.
//
// PUBLIC CONTRACT: the source re-exported `REQUIRED_FIELDS`, and existing
// imports do `import { REQUIRED_FIELDS } from '.../AddEventForm'`. That named
// export is preserved here by re-exporting the extracted logic module.
//
// Validates: Requirements 1.1, 1.2, 2.1, 2.2, 2.3

import useEventDraft from './addEventForm/hooks/useEventDraft';
import useSaveEvent from './addEventForm/hooks/useSaveEvent';
import AddEventModalShell from './addEventForm/presentation/AddEventModalShell';
import EventFields from './addEventForm/presentation/EventFields';
import AddEventFooter from './addEventForm/presentation/AddEventFooter';

const AddEventForm = ({ eventType, trip: _trip, onAddEvent, onCancel }) => {
  const draft = useEventDraft({ eventType });
  const { isSaving, handleSave } = useSaveEvent({
    eventType,
    formData: draft.formData,
    setErrors: draft.setErrors,
    onAddEvent,
  });

  return (
    <AddEventModalShell eventType={eventType} onCancel={onCancel}>
      <EventFields
        eventType={eventType}
        formData={draft.formData}
        errors={draft.errors}
        onChange={draft.handleChange}
      />
      <AddEventFooter
        eventType={eventType}
        isSaving={isSaving}
        onCancel={onCancel}
        onSave={handleSave}
      />
    </AddEventModalShell>
  );
};

export default AddEventForm;
// Preserve the public named export: existing callers (and the 13.1 contract
// test) do `import { REQUIRED_FIELDS } from '.../AddEventForm'`.
export { default as REQUIRED_FIELDS } from './addEventForm/logic/requiredFields';
